/**
 * The register's push channel (T07-06 server, T07-23 client): pos-service
 * `GET /api/v1/pos/events/stream?topics=inventory.changed,cart.changed,…` behind the realtime
 * library's `subscribe()` (`@tcg/realtime` `serviceStream` — reconnect 1 → 30 s with
 * `Last-Event-ID`, 3 failures → fallback poll every 30 s, silent stream → reopen, `resync` →
 * the screen refetches). The hub topics of R21 (`pos:register:<id>`) join in T07-40.
 *
 * | topic here | SSE event | payload |
 * |---|---|---|
 * | `pos.events:inventory.changed` | `inventory.changed` | `{variantId, unitId, available, availableQty, reason, state, locationId}` — a card of this register was sold / held elsewhere |
 * | `pos.events:cart.changed` | `cart.changed` | `{cartId, version, action, status, reason?, lineIds?}` — refetch the basket |
 * | `pos.events:hold.changed` | `hold.changed` | 保留 / 取り置き changed (T07-28) |
 * | `pos.events:sale.changed` | `sale.changed` | a sale completed / returned / voided / picked up, or its 免税 record reached tax-service (T07-25…30) |
 * | `pos.events:buylist.changed` | `buylist.changed` | 買取 changed (T07-31) |
 */
import { parseControl, serviceStream, type ChangeMessage, type SourceFactory } from "@tcg/realtime";

export const POS_EVENT_PREFIX = "pos.events:";

export const POS_EVENTS = [
  "inventory.changed",
  "cart.changed",
  "hold.changed",
  "sale.changed",
  "buylist.changed",
] as const;
export type PosEventName = (typeof POS_EVENTS)[number];

export function posTopic(event: PosEventName): string {
  return `${POS_EVENT_PREFIX}${event}`;
}

export const POS_TOPICS = {
  inventory: posTopic("inventory.changed"),
  cart: posTopic("cart.changed"),
  hold: posTopic("hold.changed"),
  sale: posTopic("sale.changed"),
  buylist: posTopic("buylist.changed"),
} as const;

export interface InventoryChange {
  variantId: string;
  unitId?: string | null;
  available: boolean;
  availableQty?: number;
  reason?: string | null;
  state?: string | null;
  locationId?: string | null;
  /** When it changed (ISO), when the stream says (inventory-service's `occurredAt`). */
  occurredAt?: string | null;
}

export interface CartChange {
  cartId: string;
  version?: number;
  action?: string;
  status?: string;
  reason?: string | null;
  lineIds?: string[];
}

const EVENTS: ReadonlySet<string> = new Set(POS_EVENTS);

function idOf(event: string, data: Record<string, unknown>): string {
  const key =
    event === "inventory.changed"
      ? data.variantId
      : event === "cart.changed"
        ? data.cartId
        : (data.holdId ?? data.saleId ?? data.intakeId);
  return typeof key === "string" ? key : "";
}

export interface PosStreamOptions {
  /** Gateway path of pos-service's stream. */
  path?: string;
}

export function posEventsStream(options: PosStreamOptions = {}): SourceFactory {
  const path = options.path ?? "/api/v1/pos/events/stream";
  return serviceStream({
    name: "pos",
    accepts: (topic) => topic.startsWith(POS_EVENT_PREFIX),
    url(topics) {
      const names = topics
        .map((t) => t.slice(POS_EVENT_PREFIX.length))
        .filter((n) => EVENTS.has(n));
      if (names.length === 0) {
        return null;
      }
      return `${path}?topics=${encodeURIComponent([...new Set(names)].sort().join(","))}`;
    },
    translate(event, topics) {
      if (event.event === "resync") {
        return { resync: true };
      }
      if (!EVENTS.has(event.event)) {
        return null; // ready / ping
      }
      const topic = `${POS_EVENT_PREFIX}${event.event}`;
      if (!topics.has(topic)) {
        return null;
      }
      const data = parseControl(event.data);
      const message: ChangeMessage = {
        topic,
        id: idOf(event.event, data),
        action: typeof data.action === "string" ? data.action : event.event,
        version: typeof data.version === "number" ? data.version : null,
        at: new Date().toISOString(),
        entity: event.event.split(".")[0],
        payload: data,
        source: "pos",
      };
      return { messages: [message] };
    },
  });
}

/** Is this inventory change "the card is gone" for a line that this basket keeps? */
export function soldElsewhere(change: InventoryChange): boolean {
  if (change.available) {
    return false;
  }
  const state = change.state ?? "";
  const reason = change.reason ?? "";
  // our own hold shows up as reserved/held — that is not "sold elsewhere"
  if (state === "reserved" || state === "held" || reason === "reserved" || reason === "held") {
    return false;
  }
  return (
    state === "sold" ||
    ["sold", "withdrawn", "live_show", "unpublished", "in_transit", "other_location"].includes(
      reason,
    )
  );
}
